import React, { useState, Dispatch, SetStateAction, useEffect } from "react";

import Grid from "@mui/material/Grid";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { FiCheck } from "react-icons/fi";
import { RiShareLine } from "react-icons/ri";

import { useVideoSurveyInventory } from "../../../../hooks/useVideoSurveyInventory";
import { Destination } from "../../../../models/destination";
import { DifferentLocation } from "../../../../models/location";
import { RoomJob } from "../../../../models/sales";
import { getDestinations } from "../../../../util/getDestinations";
import Button, { Color } from "../../../Form/Button";
import Modal from "../../../Generic/Modal";
import useVideoContext from "../../hooks/useVideoContext/useVideoContext";
import { InventoryInfo } from "../../Inventory/InventoryInfo";
import { InventoryList } from "../../Inventory/InventoryList";
import { InventoryLogoAndShared } from "../../Inventory/InventoryLogoAndShared";
import ShareInventory from "../../Inventory/ShareInventory";
import ShareLinkModal from "../../PreJoinModal/ShareLinkModal";
import useSurveyStyles from "../styles";
import ShareEstimate from "../TabSection/TabsComponent/components/Estimate/ShareEstimateModal";
import useStyles from "./styles";

const emptyInventory = require("../../../../assets/img/empty-inventory.svg");

interface Props {
  isCollapsed: boolean;
  setIsCollapsed: Dispatch<SetStateAction<boolean>>;
}

const InventorySection = ({ isCollapsed }: Props) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const surveyClasses = useSurveyStyles();

  const [openShareLinkModal, setOpenShareLinkModal] = useState<boolean>(false);
  const [openShareInventoryModal, setOpenInventoryLinkModal] = useState<boolean>(false);
  const [openShareEstimateModal, setOpenShareEstimateModal] = useState<boolean>(false);

  const [inventoryShared, setInventoryShared] = useState<boolean>(false);
  const [destinationOptions, setDestinationOptions] = useState<Destination[]>([]);

  const [estimateShared, setEstimateShared] = useState<boolean>(false);

  const { task } = useVideoContext();
  const {
    inventory,
    updateEnabled,
    updateQuoteInventory,
    updatingQuoteInventory,
    inventoryUpdated,
    destinations,
    job,
  } = useVideoSurveyInventory();

  const shareLinkTitle = (
    <div className={classes.sharingTitle}>
      <div className={classes.headerIcon}>
        <RiShareLine size={24} color="#308CDB" />
      </div>
      {translate("share")}
    </div>
  );

  const renderSaveChangesButton = () => {
    if (updateEnabled) {
      return (
        <div className={classes.saveChangeContainer}>
          <section className={classes.saveChangeSection}>
            <Button
              data-cy="saveInventoryButton"
              customColor={Color.SUCCESS}
              loading={updatingQuoteInventory}
              disabled={updatingQuoteInventory}
              onClick={() => updateQuoteInventory()}
            >
              {inventoryUpdated ? (
                <>
                  <FiCheck size={20} color="#FFF" className="mr-2" />
                  {translate("inventoryUpdated")}
                </>
              ) : (
                translate("saveChanges")
              )}
            </Button>
          </section>
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    if (destinations) {
      const destination = getDestinations(destinations, job?.destination?.locations);

      setDestinationOptions(destination);
    }
  }, [destinations]);

  return !isCollapsed ? (
    <Grid data-cy="inventorySection" container item xs={4} direction="column">
      <Grid item>
        <div className={classnames(surveyClasses.section, classes.mainSection)}>
          <div className={classes.container}>
            {renderSaveChangesButton()}
            {inventory && inventory.length > 0 ? (
              <div data-cy="roomList" className={classes.inventoryList}>
                {inventory.map((room: RoomJob, index: number) => {
                  return (
                    <InventoryList
                      key={index}
                      room={room}
                      indexRoom={index}
                      locations={job?.destination?.locations || ([] as DifferentLocation[])}
                      destinations={destinationOptions}
                    />
                  );
                })}
              </div>
            ) : (
              <div data-cy="emptyInventory" className={classes.emptyInventory}>
                <img src={emptyInventory} alt="empty-inventory" />

                <span>{`${translate("youDidnTAddAnyItemsYet")} :(`} </span>
              </div>
            )}
          </div>
        </div>
      </Grid>

      <Grid item>
        <div className={classes.weightSection}>
          <InventoryInfo />
        </div>
      </Grid>

      <Grid item>
        <div className={classes.shareSection}>
          <InventoryLogoAndShared
            openShareLinkModal={setOpenShareLinkModal}
            openShareInventoryModal={setOpenInventoryLinkModal}
            openShareEstimateModal={setOpenShareEstimateModal}
          />
        </div>
      </Grid>

      <Modal
        open={openShareInventoryModal}
        className={classes.modal}
        handleClose={() => setOpenInventoryLinkModal(false)}
        title={shareLinkTitle}
        data-cy="shareInventoryModal"
      >
        <ShareInventory
          openModal={setOpenInventoryLinkModal}
          taskId={task?.id}
          inventoryShared={inventoryShared}
          setInventoryShared={setInventoryShared}
          customer={task?.job?.customer}
        />
      </Modal>

      {openShareLinkModal && (
        <ShareLinkModal
          link={task?.roomLink || ""}
          taskId={task?.id}
          customer={task?.job?.customer}
          isOpenModal={openShareLinkModal}
          onCloseModal={() => setOpenShareLinkModal(false)}
        />
      )}

      <Modal
        open={openShareEstimateModal}
        className={classes.modal}
        handleClose={() => setOpenShareEstimateModal(false)}
        title={shareLinkTitle}
        data-cy="shareEstimateModal"
      >
        <ShareEstimate
          taskId={task?.id}
          customer={task?.job?.customer}
          estimateShared={estimateShared}
          setEstimateShared={setEstimateShared}
        />
      </Modal>
    </Grid>
  ) : null;
};

export default InventorySection;
